import {
  type KeyboardEvent,
  type PointerEvent,
  useState,
} from 'react';

// a local setting, never synced, in rem so it follows the font size
const KEY = 'sidebarWidth';
const DEFAULT = 17.5;
const MIN = 12;
const STEP = 1;

const remPx = () =>
  parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

// at most half the window, so the note always keeps room
const clamp = (rem: number) =>
  Math.min(Math.max(rem, MIN), Math.max(MIN, innerWidth / remPx() / 2));

// storage can be off, or throw, in a private window
const saved = () => {
  try {
    const rem = Number(localStorage.getItem(KEY));
    return rem > 0 ? clamp(rem) : DEFAULT;
  } catch {
    return DEFAULT;
  }
};

const save = (rem: number) => {
  try {
    if (rem === DEFAULT) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, String(rem));
  } catch {}
};

// the sidebar's width, as remembered on this device
export const useSidebarWidth = () => useState(saved);

// a handle on the sidebar's edge: dragged, or moved with the arrow keys, it
// sets the width; a double click puts the default back. A phone has no
// sidebar beside the note, so the stylesheet hides it there
export const SidebarResizer = (
  { width, onWidth }: { width: number, onWidth: (rem: number) => void },
) => {
  const set = (rem: number) => {
    const next = clamp(rem);
    onWidth(next);
    return next;
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const from = width;
    let last = from;
    const move = (m: globalThis.PointerEvent) => {
      last = set(from + (m.clientX - startX) / remPx());
    };
    const up = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      document.body.classList.remove('resizing');
      save(last);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
    document.body.classList.add('resizing');
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const by = e.key === 'ArrowLeft'
      ? -STEP
      : e.key === 'ArrowRight'
      ? STEP
      : 0;
    if (!by) return;
    e.preventDefault();
    save(set(width + by));
  };

  return (
    <div
      className="sidebar-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the note list"
      aria-valuenow={Math.round(width)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => save(set(DEFAULT))} />
  );
};
